import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";

import SuiTypography from "components/SuiTypography";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";

import ProyeccionPDFDialog from "./proyeccionPDFDialog";

import ProcesosApi from "api/procesosApi";
import BucketApi from "api/bucketApi";
import PDFApi from "api/pdfApi";

const ProyeccionHeader = ({
  procesoId,
  identificador,
  fechaInicio,
  setFechaInicio,
  onReiniciar,
  tieneProyeccion,
  propuestaId,
  tipo_propuesta,
}) => {
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [showPdf, setShowPdf] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfExists, setPdfExists] = useState(false);
  let FILE_NAME = "";
  let ANEXO_TYPE = "";
  let FILE_GENERATE = "";
  let NOMBRE_ANEXO = "";
  let incluirPdf = false;
  switch (tipo_propuesta) {
    case "solicitud":
      FILE_NAME = "pdf_proyeccion.pdf";
      ANEXO_TYPE = "PDF_PROYECCION";
      FILE_GENERATE = "proyeccion_solicitud";
      NOMBRE_ANEXO = "Proyección Solicitud";
      incluirPdf = true;
      break;
    case "conciliacion":
      FILE_NAME = "pdf_proyeccion_conciliada.pdf";
      ANEXO_TYPE = "PDF_PROYECCION_CONCILIADA";
      FILE_GENERATE = "proyeccion_conciliada";
      NOMBRE_ANEXO = "Proyección Conciliación";
      incluirPdf = false;
      break;
    case "acuerdo":
      FILE_NAME = "pdf_proyeccion_acuerdo.pdf";
      ANEXO_TYPE = "PDF_PROYECCION_ACUERDO";
      FILE_GENERATE = "proyeccion_acuerdo";
      NOMBRE_ANEXO = "Proyección Acuerdo";
      incluirPdf = false;
      break;
    default:
      console.warn(`Tipo de propuesta no reconocido: ${tipo_propuesta}`);
  }

  useEffect(() => {
    if (identificador) {
      checkPdfExists();
    }
  }, [identificador]);

  const checkPdfExists = async () => {
    try {
      if (!identificador) return false;

      const response = await BucketApi.checkProcesoFileExists(FILE_NAME, identificador);
      const exists = response.data.exists;
      setPdfExists(exists);
      if (exists) {
        //const urlResponse = await BucketApi.getProcesoFileUrl(FILE_NAME, identificador);
        const urlResponse = await BucketApi.getProcesoFileUrlFresh(FILE_NAME, identificador);
        setPdfUrl(urlResponse.data.file_url);
      }
      return exists;
    } catch (error) {
      console.error("Error verificando PDF:", error);
      setPdfExists(false);
      return false;
    }
  };

  const handleGenerarPDF = async () => {
    try {
      if (!propuestaId) {
        throw new Error("No hay una propuesta activa para generar el PDF");
      }
      setLoadingPdf(true);
      if (pdfExists) {
        const anexoResponse = await ProcesosApi.obtenerAnexoProcesoByTipo(procesoId, {
          tipo_anexo: ANEXO_TYPE,
        });
        const anexo = anexoResponse.data;
        if (anexo !== undefined && !anexoResponse.data.error) {
          await ProcesosApi.eliminarAnexoProceso(procesoId, anexo.id);
        }
      }
      const pdfResponse = await PDFApi.generateDocument(FILE_GENERATE, propuestaId, {
        responseType: "blob",
      });
      const pdfBlob = new Blob([pdfResponse.data], { type: "application/pdf" });
      const formData = new FormData();
      formData.append("file", pdfBlob, FILE_NAME);
      formData.append("file_type", "proceso");
      formData.append("proceso_id", identificador);
      formData.append("registrar_anexo", "true");
      formData.append("tipo_anexo", ANEXO_TYPE);
      formData.append("nombre_anexo", NOMBRE_ANEXO);
      if (incluirPdf) {
        formData.append("incluir_en_pdf", "True");
      }
      await BucketApi.uploadFile(formData);
      await checkPdfExists();
      const url = URL.createObjectURL(pdfBlob);
      setPdfUrl(url);
      setShowPdf(true);
    } catch (error) {
      console.error("Error al generar PDF:", error);
    } finally {
      setLoadingPdf(false);
    }
  };

  const handleReiniciarClick = () => {
    setOpenConfirmDialog(true);
  };

  const handleConfirmReiniciar = () => {
    setOpenConfirmDialog(false);

    // Establecer fecha al día 01 del mes SIGUIENTE para reinicio
    const nuevoInicio = dayjs().add(1, "month").startOf("month");
    setFechaInicio(nuevoInicio);

    onReiniciar();
  };

  return (
    <>
      <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
        <Grid size={12}>
          <SuiTypography variant="h3" fontWeight="bold">
            Proyección de Pagos: {tipo_propuesta?.toUpperCase()}
          </SuiTypography>
        </Grid>
      </Grid>
      <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }} sx={{ mt: 1 }}>
        <Grid size={6} display="flex" justifyContent="center" alignItems="center">
          <SuiTypography variant="body2" fontWeight="light" sx={{ mr: 2 }}>
            Inicio Pagos
          </SuiTypography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={fechaInicio}
              onChange={setFechaInicio}
              format="DD/MM/YYYY"
              minDate={dayjs()}
              shouldDisableDate={(date) => {
                const dia = date.date();
                return dia > 27;
              }}
              slotProps={{
                textField: {
                  helperText: "Seleccione un día entre el 1er y 27",
                },
              }}
              renderInput={(params) => (
                <SuiInput
                  {...params}
                  placeholder="Fecha de Inicio de Pagos"
                  icon={{
                    component: <CalendarTodayIcon />,
                    direction: "right",
                  }}
                />
              )}
            />
          </LocalizationProvider>
        </Grid>
        <Grid size={6} display="flex" justifyContent="center" alignItems="center">
          <SuiButton
            variant="outlined"
            buttonColor="error"
            size="small"
            onClick={handleReiniciarClick}
            startIcon={<RestartAltIcon sx={{ fontSize: "inherit" }} />}
            sx={{ mr: 2 }}
          >
            Reiniciar
          </SuiButton>
          {tieneProyeccion && propuestaId && (
            <>
              {pdfExists ? (
                <>
                  <SuiButton
                    variant="gradient"
                    buttonColor="info"
                    onClick={() => {
                      setShowPdf(true);
                    }}
                    startIcon={<VisibilityIcon />}
                    sx={{ mr: 2 }}
                  >
                    Ver PDF
                  </SuiButton>
                  <SuiButton
                    variant="gradient"
                    buttonColor="secondary"
                    onClick={handleGenerarPDF}
                    disabled={loadingPdf}
                    startIcon={<PictureAsPdfIcon sx={{ fontSize: "inherit" }} />}
                  >
                    {loadingPdf ? "Regenerando..." : "Regenerar PDF"}
                  </SuiButton>
                </>
              ) : (
                <SuiButton
                  variant="gradient"
                  buttonColor="success"
                  onClick={handleGenerarPDF}
                  disabled={loadingPdf}
                  startIcon={<PictureAsPdfIcon sx={{ fontSize: "inherit" }} />}
                >
                  {loadingPdf ? "Generando..." : "Generar PDF"}
                </SuiButton>
              )}
            </>
          )}
        </Grid>
      </Grid>
      <Dialog
        open={openConfirmDialog}
        onClose={() => setOpenConfirmDialog(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Confirmar Reinicio dela Proyección</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            ¿Está seguro(a) que desea reiniciar la proyección? Esta acción eliminará todas las
            modificaciones, vinculaciones y configuraciones actuales, restableciendo los valores
            predeterminados (tasa de interés 0% y cuota base según el recurso disponible).
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <SuiButton
            variant="text"
            buttonColor="secondary"
            onClick={() => setOpenConfirmDialog(false)}
          >
            Cancelar
          </SuiButton>
          <SuiButton
            variant="contained"
            buttonColor="error"
            onClick={handleConfirmReiniciar}
            autoFocus
          >
            Reiniciar
          </SuiButton>
        </DialogActions>
      </Dialog>

      <ProyeccionPDFDialog
        open={showPdf}
        onClose={() => {
          setShowPdf(false);
          if (pdfUrl) {
            window.URL.revokeObjectURL(pdfUrl);
            setPdfUrl(null);
          }
        }}
        pdfUrl={pdfUrl}
      />
    </>
  );
};

ProyeccionHeader.propTypes = {
  procesoId: PropTypes.number.isRequired,
  identificador: PropTypes.string.isRequired,
  fechaInicio: PropTypes.object.isRequired,
  setFechaInicio: PropTypes.func.isRequired,
  onReiniciar: PropTypes.func.isRequired,
  tieneProyeccion: PropTypes.bool.isRequired,
  propuestaId: PropTypes.number,
  tipo_propuesta: PropTypes.string,
};

export default ProyeccionHeader;
